import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { useIntl, defineMessages } from 'dibs-react-intl';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import Checkmark from 'dibs-icons/exports/legacy/Checkmark';
import FailedIcon from 'dibs-icons/exports/legacy/UploadFailed';
import XMark from 'dibs-icons/exports/legacy/XMark';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

import style from './style.scss';

import requestShippingFlow from './flows/requestShipping';
import requestHoldFlow from './flows/requestHold';
import transferOfferFlow from './flows/transferOffer';

import {
    BulkOperationResultDisplay_bulkOperationResult$key,
    BulkOperationResultDisplay_bulkOperationResult$data as Result,
} from './__generated__/BulkOperationResultDisplay_bulkOperationResult.graphql';

/*
    can't import the following otherwise a circular dependency
    is created and the build fails
    (yet to be confirmed, but the following error happens at build:
        15:53:55 Build failed:  RangeError: Maximum call stack size exceeded
        15:53:57     at findDependencies (/jenkins/workspace/ferrum-pullrequest/scripts/helpers/getDependantMap.ts:15:1)
        ...
    )
// todo: figure out better way to import this type ... generic component?
import { bulkTransferBundleMutationResponse } from 'dibs-transfer-offer/src/mutations/__generated__/bulkTransferBundleMutation.graphql';
*/

type ItemFailed = NonNullable<ArrayElement<Result['itemsFailed']>>;

type FailureReasons = { [key: string]: ReactNode };

export type RequestFlowType = 'REQUEST_HOLD' | 'REQUEST_SHIPPING' | 'TRANSFER_OFFER';
type RequestFlowCopyReturnType = {
    barHeaderTitleSent?: ReactNode;
    barHeaderTitleFailed?: ReactNode;
    barHeaderSubtitle?: ReactNode;
    successHeadline?: ReactNode;
    failHeadline?: ReactNode;
    failureReasons?: FailureReasons;
};

// these two types should be reviewed when the ConversationsType is created properly
// type BulkTransferBundleType = NonNullable<bulkTransferBundleMutationResponse['bulkTransferBundle']>;
// type ConversationsType = BulkTransferBundleType['conversations'];

type PrimaryProps = {
    flowType?: RequestFlowType;
    bulkOperationResult: BulkOperationResultDisplay_bulkOperationResult$key | null;
    conversations?: $TSFixMe; //ConversationsType;
};

type ItemListFailureReason = {
    itemId: string | null;
    text: ReactNode;
};

type ItemsSucceeded = Result['itemsSucceeded'];
type ItemsFailed = Result['itemsFailed'];

type ItemListProps = {
    headline: ReactNode;
    items?: ItemsSucceeded | ItemsFailed;
    iconType: 'success' | 'failure';
    failureReasons?: ItemListFailureReason[];
    dataTn: string;
};

const messages = defineMessages({
    titlePostfix: {
        id: 'dibs.bulkOperationResultDisplay.titlePostfix',
        defaultMessage: 'to {sellerName}',
    },
    title: {
        id: 'dibs.bulkOperationResultDisplay.title',
        defaultMessage: 'General Inquiry {titlePostfix}',
    },
});

const Icon: FC<{ type: ItemListProps['iconType'] }> = ({ type }) => {
    return (
        <span className={style.iconWrapper}>
            {type === 'success' && <Checkmark className={style.checkMark} />}
            {type === 'failure' && <XMark className={style.xMark} />}
        </span>
    );
};

const ItemList: FC<ItemListProps> = ({
    headline,
    items,
    iconType,
    failureReasons = [],
    dataTn,
}: ItemListProps) => {
    if (!items || (items && !items.length)) {
        return null;
    }
    return (
        <>
            <div className={style.headline} data-tn="request-headline">
                {headline}
            </div>

            <ul className={style.list} data-tn={dataTn}>
                {items.map(item => {
                    const serviceId = item?.serviceId;
                    const title = item?.title;
                    const failureReasonObj = failureReasons.find(r => r.itemId === serviceId);
                    const failureReasonText = failureReasonObj?.text;
                    return (
                        <li key={serviceId || title} className={style.listItem}>
                            <Icon type={iconType} />
                            <div className={style.titleWrapper} data-tn="request-item-title">
                                {title}
                                {failureReasonText && (
                                    <div
                                        className={style.failureReasonWrapper}
                                        data-tn="request-failure-message"
                                    >
                                        {failureReasonText}
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

function getFlow(flowType?: RequestFlowType): RequestFlowCopyReturnType {
    switch (flowType) {
        case 'REQUEST_SHIPPING':
            return requestShippingFlow;

        case 'REQUEST_HOLD':
            return requestHoldFlow;

        case 'TRANSFER_OFFER':
            return transferOfferFlow;
    }
    return {};
}

const BulkOperationResultDisplay: FC<PrimaryProps> = ({
    flowType,
    bulkOperationResult: bulkOperationResultRef,
    conversations,
}) => {
    const intl = useIntl();
    const bulkOperationResult = useFragment(
        graphql`
            fragment BulkOperationResultDisplay_bulkOperationResult on BulkOperationResult {
                itemsSucceeded {
                    title
                    serviceId
                    hasRequestedHoldForBuyer(buyerId: $userId)
                    projects(userId: $userId) {
                        serviceId
                        name
                    }
                }
                itemsFailed {
                    title
                    serviceId
                    projects(userId: $userId) {
                        serviceId
                        name
                    }
                }
                failureCodes {
                    itemId
                    failureCode
                }
            }
        `,
        bulkOperationResultRef
    );
    const {
        barHeaderTitleSent,
        barHeaderTitleFailed,
        barHeaderSubtitle,
        successHeadline,
        failHeadline,
        failureReasons = {},
    } = getFlow(flowType);

    /**
     * itemIds should be the same as items associated to conversations except when it's a general inquiry and there is no item in conversation.
     * In latter case we create `item` and `failureCode` objects with pre-specified item title and failure code.
     */
    const genInquiryItems: ItemFailed[] = [];
    const genInquiryFailureCodes: { failureCode: 'GENERAL_INQUIRY'; itemId: null }[] = [];
    if (conversations && conversations.length) {
        conversations.forEach((conv: $TSFixMe) => {
            if (conv && !conv.item) {
                const sellerName = conv?.sellerMember?.seller?.sellerProfile?.company;
                const titlePostfix = sellerName
                    ? intl.formatMessage(messages.titlePostfix, { sellerName })
                    : '';
                genInquiryItems.push({
                    title: intl.formatMessage(messages.title, { titlePostfix }),
                    serviceId: null,
                    projects: null,
                });
                genInquiryFailureCodes.push({
                    itemId: null,
                    failureCode: 'GENERAL_INQUIRY',
                });
            }
        });
    }

    const itemsFailed = (bulkOperationResult?.itemsFailed || []).concat(genInquiryItems);
    const failureCodes = (bulkOperationResult?.failureCodes || []).concat(genInquiryFailureCodes);
    const itemsSucceeded = bulkOperationResult?.itemsSucceeded || [];

    const mapFailureCodeToReason: {
        itemId: string | null;
        text: ReactNode;
    }[] = failureCodes.filter(filterNulls).map(({ failureCode, itemId }) => ({
        itemId,
        text:
            failureCode && failureReasons[failureCode]
                ? failureReasons[failureCode]
                : failureReasons.SYSTEM_ERROR,
    }));

    const showFailedHeader =
        itemsFailed && itemsFailed.length && !(itemsSucceeded && itemsSucceeded.length);
    const title = showFailedHeader ? barHeaderTitleFailed : barHeaderTitleSent;
    const subtitle = showFailedHeader ? '' : barHeaderSubtitle;

    return (
        <div data-tn="shipping-request-modal-bulk-items-success">
            {showFailedHeader ? (
                <FailedIcon className={style.failedIcon} />
            ) : (
                <Checkmark className={style.successIcon} />
            )}
            <BarHeader title={title || ''} subtitle={subtitle} />

            <ModalBody>
                <ItemList
                    dataTn="request-item-list-success"
                    headline={successHeadline}
                    items={itemsSucceeded}
                    iconType="success"
                />

                <ItemList
                    dataTn="request-item-list-failed"
                    headline={failHeadline}
                    items={itemsFailed}
                    iconType="failure"
                    failureReasons={mapFailureCodeToReason}
                />
            </ModalBody>
        </div>
    );
};

export default BulkOperationResultDisplay;
